import fileConverter from '@/mixins/fileConverter';
import { computed, Ref, ref } from 'vue';

interface FileSize {
  maxFileSizeFromProps: number;
  permittedTotalFileSizeFromProps: number;
  currentTotalFileSizeFromProps: number;
}

interface FileType {
  allowedMimeTypes: string[];
}

export interface FileUpload {
  fileType: FileType;
  fileSize: FileSize;
}

const UPLOAD_ERROR_TOO_BIG = 'too_big';
const UPLOAD_ERROR_WRONG_TYPE = 'wrong_type';
const UPLOAD_ERROR_TOTAL_SIZE_EXCEEDED = 'total_size_exceeded';

const DEFAULT_MAX_FILE_SIZE = 6291456; // 6 MB

const useFileUpload = (fileUploadConfig: Ref<FileUpload>) => {
  const input = document.createElement('input');
  input.type = 'file';

  const allowedMimeTypes = computed(() => fileUploadConfig.value.fileType.allowedMimeTypes);
  const currentTotalFileSizeFromProps = computed(() => fileUploadConfig.value.fileSize.currentTotalFileSizeFromProps);
  const permittedTotalFileSizeFromProps = computed(() => fileUploadConfig.value.fileSize.permittedTotalFileSizeFromProps);
  const maxFileSizeFromProps = computed(() => fileUploadConfig.value.fileSize.maxFileSizeFromProps);

  const maxFileSize = maxFileSizeFromProps.value || DEFAULT_MAX_FILE_SIZE;
  const maxFileSizeMB = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.floor(maxFileSize / 1000000));

  const fileContents = ref<string>('');
  const fileName = ref<string>('');
  const mimeType = ref<string>('');
  const error = ref<string>('');

  input.onchange = async (e) => {
    error.value = '';
    fileContents.value = '';
    mimeType.value = '';
    fileName.value = '';
    const file:File = (e.target as any).files[0];

    if (file.size > maxFileSize) {
      error.value = UPLOAD_ERROR_TOO_BIG;
    } else {
      const {
        string,
        mime,
      } = await fileConverter.methods.fileToString(file);
      if (allowedMimeTypes.value.length && (!mime || !allowedMimeTypes.value.includes(mime))) {
        error.value = UPLOAD_ERROR_WRONG_TYPE;
      } else if (currentTotalFileSizeFromProps.value + file.size > permittedTotalFileSizeFromProps.value) {
        error.value = UPLOAD_ERROR_TOTAL_SIZE_EXCEEDED;
      } else {
        fileContents.value = string;
        mimeType.value = mime ?? '';
        fileName.value = file.name;
      }
    }
  };

  const upload = () => {
    error.value = '';
    input.value = '';
    input.click();
  };

  return {
    upload,
    fileContents,
    fileName,
    mimeType,
    error,
    maxFileSize,
    maxFileSizeMB,
  };
};

export default useFileUpload;
